import type { ParsedLevel, PlayerRuntime, Vec2 } from '@md/engine';
import {
  MAGNET_GROUND_GRIP,
  MAGNET_RAY_STEP_TILES,
  MAGNET_WALL_MAX_THICKNESS_TILES,
} from '@md/shared';

/**
 * Boîte à outils des forces magnétiques — partagée par les règles
 * d'interaction, les blocs polarisés et les électroaimants.
 *
 * Toutes les grandeurs sont en tuiles et tuiles/s, et aucun appel à
 * `Math.random()` ni `Date.now()` : ces fonctions font partie de la simulation
 * déterministe (spec §3).
 */

/** Résultat d'un calcul de force : direction unitaire + intensité. */
export interface MagneticForce {
  readonly ux: number;
  readonly uy: number;
  readonly accel: number;
  readonly distance: number;
}

/** Loi de force du jeu : `a(d) = clamp(k / max(d, dMin), 0, aMax)` (spec §3). */
export function magneticAccel(
  distance: number,
  constant: number,
  minDistance: number,
  maxAccel: number,
): number {
  return Math.min(maxAccel, constant / Math.max(distance, minDistance));
}

/**
 * Longueur du plus long segment SOLIDE traversé par le trait `a → b`.
 *
 * Sert à la règle « le champ traverse les murs fins » (concept §3.1) : un
 * grillage d'une tuile laisse passer, une paroi épaisse coupe. L'échantillon-
 * nage à pas fixe garde le résultat strictement reproductible.
 */
export function solidRunLength(level: ParsedLevel, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy);
  if (length === 0) {
    return 0;
  }
  const steps = Math.ceil(length / MAGNET_RAY_STEP_TILES);
  const stepLength = length / steps;
  let longest = 0;
  let current = 0;
  for (let i = 0; i <= steps; i += 1) {
    const t = i / steps;
    const x = a.x + dx * t;
    const y = a.y + dy * t;
    if (level.tileAt(Math.floor(x), Math.floor(y)).solid) {
      current += stepLength;
      longest = Math.max(longest, current);
    } else {
      current = 0;
    }
  }
  return longest;
}

/** Le champ passe-t-il entre ces deux points (murs fins uniquement) ? */
export function fieldReaches(level: ParsedLevel, a: Vec2, b: Vec2): boolean {
  return solidRunLength(level, a, b) <= MAGNET_WALL_MAX_THICKNESS_TILES;
}

/**
 * Fraction de force reçue par un robot, par axe.
 *
 * - **accroché** à une surface : totalement ancré, il ne reçoit rien — c'est
 *   ce qui permet de hisser son partenaire (concept §3.1) ;
 * - **au sol** : l'adhérence absorbe une partie de la composante horizontale ;
 * - **en l'air** : force pleine.
 */
export function receptionFactors(player: PlayerRuntime): { x: number; y: number } {
  if (player.flags.clinging === 1) {
    return { x: 0, y: 0 };
  }
  if (player.body.onGround) {
    return { x: MAGNET_GROUND_GRIP, y: 1 };
  }
  return { x: 1, y: 1 };
}

/**
 * Ajoute une accélération à une vitesse, en bornant la composante ACQUISE le
 * long de la force. Le clamp ne touche pas aux autres composantes : un robot
 * peut toujours tomber à `MAX_FALL_SPEED` ou sauter à `JUMP_SPEED`.
 */
export function applyAccel(
  velocity: { vx: number; vy: number },
  ux: number,
  uy: number,
  accel: number,
  dt: number,
  maxSpeed: number,
  factorX = 1,
  factorY = 1,
): void {
  velocity.vx += ux * accel * dt * factorX;
  velocity.vy += uy * accel * dt * factorY;
  const along = velocity.vx * ux + velocity.vy * uy;
  if (along > maxSpeed) {
    const excess = along - maxSpeed;
    velocity.vx -= ux * excess;
    velocity.vy -= uy * excess;
  }
}

/** Direction unitaire de `from` vers `to`, ou `null` si les points coïncident. */
export function direction(from: Vec2, to: Vec2): { ux: number; uy: number; d: number } | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const d = Math.hypot(dx, dy);
  if (d === 0) {
    return null;
  }
  return { ux: dx / d, uy: dy / d, d };
}
